import React, { useState } from "react";
import {
	ActionBar,
	Popover,
	Select,
	Form,
	FormElementWrapper,
	InputField,
	Radios,
	ThemeContextProps,
	Theme,
	getThemes,
} from "../../lab/src";
import { Content, Divider, FancyContextProps, Icon, IconSettings } from "../../lib/src";
import type { Language } from "../../lib/src/context/translations";

export type ExampleContextProps = FancyContextProps & ThemeContextProps;

export function ContextControls(props: {
	value: ExampleContextProps;
	onChange: (p: ExampleContextProps) => void;
}): JSX.Element {
	const [lang, setLang] = useState<Language>(props.value.lang);
	const [formattingLang, setFormat] = useState(props.value.formattingLang);
	const [staticMode, setStaticMode] = useState<"false" | "true">(
		!!props.value.staticMode ? "true" : "false"
	);
	const [theme, setTheme] = useState<Theme>(props.value.theme);

	/* eslint-disable @typescript-eslint/no-explicit-any */
	const formatter =
		typeof (Intl as any).DisplayNames !== "undefined"
			? new (Intl as any).DisplayNames([], { type: "language" })
			: { of: (x: string) => x };
	// eslint-enable @typescript-eslint/no-explicit-any

	return (
		<Popover
			aria-label="Settings"
			placement="top-end"
			hideChevron
			buttonContent={
				<Icon>
					<IconSettings />
				</Icon>
			}
			popoverContent={(id, ref, { setIsOpen }) => (
				<div id={id} style={{ maxWidth: 240 }}>
					<Content>
						<Form
							onSubmit={(e) => {
								e.preventDefault();
								props.onChange({
									formattingLang,
									lang,
									staticMode: staticMode === "true",
									theme,
								});
								setIsOpen(false);
							}}
						>
							<FormElementWrapper label={"Language"} name={"lang"}>
								<Select<Language>
									items={[
										"da-DK",
										"de-DE",
										"en-GB",
										"en-US",
										"es-ES",
										"fi-FI",
										"fr-FR",
										"it-IT",
										"ja-JP",
										"nb-NO",
										"nl-NL",
										"pt-BR",
									].map((x) => ({ value: x as Language, title: formatter.of(x) }))}
									value={lang}
									onChange={(x) => setLang(x!)}
									buttonRef={ref}
									hideClearButton
									fullWidth
									aria-label="Language"
								/>
							</FormElementWrapper>
							<FormElementWrapper
								label={"Format"}
								name={"format"}
								helptext={"Country format for numbers, dates, and time"}
								helptextPosition="above"
							>
								<InputField fullWidth value={formattingLang} onChange={setFormat} />
							</FormElementWrapper>
							<FormElementWrapper
								label={"Static mode"}
								name={"static"}
								helptext={"Renders interactive components non-interactive (used in reports)"}
								helptextPosition="above"
							>
								<Radios
									aria-label="Aria label for radio buttons"
									onChange={setStaticMode}
									value={staticMode}
								>
									<Radios.Radio value="false">Off</Radios.Radio>
									<Radios.Radio value="true">On</Radios.Radio>
								</Radios>
							</FormElementWrapper>
							<FormElementWrapper label={"Theme"} name={"theme"}>
								<Select<Theme>
									items={getThemes().map((x) => ({ value: x, title: x }))}
									value={theme}
									onChange={(x) => setTheme(x!)}
									buttonRef={ref}
									defaultOption="default"
									fullWidth
									aria-label="Theme"
								/>
							</FormElementWrapper>
						</Form>
					</Content>
					<Divider />
					<ActionBar
						primary={{
							children: "Apply context",
							onClick: () => {
								props.onChange({
									formattingLang,
									lang,
									staticMode: staticMode === "true",
									theme,
								});
								setIsOpen(false);
							},
						}}
						cancel={{
							children: "Cancel",
							onClick: () => {
								setIsOpen(false);
								setLang(props.value.lang);
								setFormat(props.value.formattingLang);
								setStaticMode(!!props.value.staticMode ? "true" : "false");
							},
						}}
					/>
				</div>
			)}
		/>
	);
}
